import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Plus, CalendarDays, Camera, Sparkles, Users, LayoutGrid, List, KanbanSquare,
  Trash2, Link2, FilePlus2, RotateCcw, ExternalLink, CheckCircle2, X, History,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { HighlightDrawer } from "@/components/piloting/HighlightDrawer";
import { ShootingDrawer } from "@/components/piloting/ShootingDrawer";
import { ShootingsView } from "@/components/piloting/ShootingsView";
import { SlotPicker, fmtDur, PubProgressBar, EVENT_STAGES, eventStageKey, isCreated } from "@/components/piloting/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const TYPE_META = {
  reel: { label: "Reel", cls: "bg-rose-50 text-rose-600 border-rose-200" },
  photo: { label: "Photo", cls: "bg-sky-50 text-sky-600 border-sky-200" },
  carrousel: { label: "Carrousel", cls: "bg-violet-50 text-violet-600 border-violet-200" },
  story: { label: "Story", cls: "bg-amber-50 text-amber-600 border-amber-200" },
  newsletter: { label: "Newsletter", cls: "bg-teal-50 text-teal-600 border-teal-200" },
  publicite: { label: "Publicité", cls: "bg-orange-50 text-orange-600 border-orange-200" },
  autre: { label: "Autre", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
};
const STATUS_META = {
  a_creer: { label: "À créer", cls: "bg-neutral-100 text-neutral-600 border-neutral-200" },
  shooting_a_programmer: { label: "Shooting à programmer", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  shooting_programme: { label: "Shooting programmé", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  en_production: { label: "En production", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  finalise: { label: "Finalisé", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  pret_a_publier: { label: "Prêt à publier", cls: "bg-green-50 text-green-700 border-green-200" },
};
const PUB_META = {
  non_creee: { label: "Non créée", cls: "text-neutral-300" },
  en_preparation: { label: "En préparation", cls: "text-neutral-500" },
  a_valider: { label: "À valider", cls: "text-amber-600" },
  modifications: { label: "Modifs demandées", cls: "text-orange-600" },
  validee: { label: "Validée", cls: "text-emerald-600" },
  programmee: { label: "Programmée", cls: "text-blue-600" },
  publiee: { label: "Publiée", cls: "text-green-700" },
  annulee: { label: "Annulée", cls: "text-red-400" },
};
const HL_TYPES = { evenement: "Événement", offre: "Offre mensuelle", campagne: "Campagne", prestation: "Prestation / sujet", saison: "Opération saisonnière", objectif: "Objectif de communication" };
const HL_STATUS = { a_preparer: "À préparer", en_cours: "En cours", termine: "Terminé" };
const fmtD = (d) => (d ? new Date(d + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "À définir");
const monthKey = (offset = 0) => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth() + offset, 1); };
const isoMonth = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

const Badge = ({ meta, value }) => {
  const m = meta[value] || { label: value, cls: "bg-neutral-100 text-neutral-500 border-neutral-200" };
  return <span className={`text-[10px] font-medium border rounded-full px-2 py-0.5 whitespace-nowrap ${m.cls}`}>{m.label}</span>;
};

const Label = ({ children }) => <label className="block text-[11px] font-medium text-neutral-500 mb-1">{children}</label>;
const sel = "w-full border border-neutral-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#1D4ED8]/30";

function Modal({ title, onClose, children, testid, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div className={`bg-white rounded-2xl p-6 w-full ${wide ? "max-w-2xl" : "max-w-md"} max-h-[90vh] overflow-y-auto space-y-3`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold">{title}</h3>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-700"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ---------------- Modales de création/édition ---------------- */

function HighlightModal({ clients, highlight, defaultClient, shootings = [], onClose, onSaved }) {
  const h = highlight || {};
  const [f, setF] = useState({
    client_id: h.client_id || defaultClient || "", name: h.name || "", type: h.type || "evenement",
    event_date: h.event_date || "", objective: h.objective || "", description: h.description || "",
    production_deadline: h.production_deadline || "", responsible: h.responsible || "",
    target_audience: h.target_audience || "", offer: h.offer || "", diffusion_date: h.diffusion_date || "",
    shooting_needed: !!h.shooting_needed, shooting_date: h.shooting_date || "",
    shooting_mode: "later", existing_shooting_id: "", new_slot: null,
    month: h.month || isoMonth(monthKey(0)), notes: h.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const save = async () => {
    if (!f.client_id || !f.name.trim()) { toast.error("Client et nom requis"); return; }
    setBusy(true);
    try {
      if (h.id) {
        await api.patch(`/content-piloting/highlights/${h.id}`, f);
      } else {
        const res = await api.post("/content-piloting/highlights", f);
        const hid = res.data.id;
        if (f.shooting_needed && f.shooting_mode === "existing" && f.existing_shooting_id) {
          await api.post(`/content-piloting/shootings/${f.existing_shooting_id}/attach-highlight`, { highlight_id: hid });
        } else if (f.shooting_needed && f.shooting_mode === "new" && f.new_slot) {
          const s = await api.post("/content-piloting/shootings", { client_id: f.client_id, date: f.new_slot.date, time: f.new_slot.start, duration_min: 120 });
          await api.post(`/content-piloting/shootings/${s.data.id}/attach-highlight`, { highlight_id: hid });
        }
      }
      toast.success(h.id ? "Temps fort mis à jour" : "Temps fort créé"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={h.id ? "Modifier le temps fort" : "Nouveau temps fort"} onClose={onClose} testid="highlight-modal" wide>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Client</Label>
          <select className={sel} value={f.client_id} onChange={set("client_id")} data-testid="hl-client">
            <option value="">—</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div><Label>Type</Label>
          <select className={sel} value={f.type} onChange={set("type")} data-testid="hl-type">
            {Object.entries(HL_TYPES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select></div>
      </div>
      <div><Label>Nom</Label><Input value={f.name} onChange={set("name")} placeholder="Ex : Soirée Gatsby, Offre mars IPL…" data-testid="hl-name" /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>Date de l'événement</Label><Input type="date" value={f.event_date} onChange={set("event_date")} data-testid="hl-event-date" /></div>
        <div><Label>Date limite de production</Label><Input type="date" value={f.production_deadline} onChange={set("production_deadline")} data-testid="hl-deadline" /></div>
        <div><Label>Mois de pilotage</Label><Input type="month" value={f.month} onChange={set("month")} data-testid="hl-month" /></div>
      </div>
      <div><Label>Objectif de communication</Label><Input value={f.objective} onChange={set("objective")} placeholder="Ex : remplir la soirée, promouvoir l'IPL…" data-testid="hl-objective" /></div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Clientèle cible</Label>
          <select className={sel} value={f.target_audience} onChange={set("target_audience")} data-testid="hl-audience">
            <option value="">—</option><option value="femmes">Femmes</option><option value="hommes">Hommes</option><option value="mixte">Mixte</option>
          </select></div>
        <div><Label>Offre commerciale éventuelle</Label><Input value={f.offer} onChange={set("offer")} placeholder="Ex : -20 % sur la cure…" data-testid="hl-offer" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 items-end">
        <div><Label>Date de diffusion souhaitée</Label><Input type="date" value={f.diffusion_date} onChange={set("diffusion_date")} data-testid="hl-diffusion" /></div>
        <div><Label>Responsable</Label><Input value={f.responsible} onChange={set("responsible")} data-testid="hl-responsible" /></div>
      </div>
      <label className="flex items-center gap-2 text-sm text-neutral-600" data-testid="hl-shooting-needed-wrap">
        <input type="checkbox" className="rounded" checked={f.shooting_needed} onChange={(e) => setF({ ...f, shooting_needed: e.target.checked })} data-testid="hl-shooting-needed" />
        Shooting nécessaire
      </label>
      {f.shooting_needed && (
        <div className="border border-amber-100 bg-amber-50/40 rounded-xl p-3 space-y-2" data-testid="hl-shooting-options">
          <Label>Organisation du shooting</Label>
          {[["later", "Décider plus tard"], ["existing", "Rattacher à un shooting existant"], ["new", "Planifier un nouveau shooting"]].map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-xs text-neutral-600 cursor-pointer">
              <input type="radio" name="hl-shooting-mode" checked={f.shooting_mode === v} onChange={() => setF({ ...f, shooting_mode: v })} data-testid={`hl-shooting-mode-${v}`} /> {l}
            </label>
          ))}
          {f.shooting_mode === "existing" && (
            <select className={sel} value={f.existing_shooting_id} onChange={set("existing_shooting_id")} data-testid="hl-existing-shooting">
              <option value="">— Choisir un shooting du client —</option>
              {shootings.filter((s) => s.client_id === f.client_id && s.status !== "annule" && s.date >= new Date().toISOString().slice(0, 10)).map((s) => (
                <option key={s.id} value={s.id}>{fmtD(s.date)}{s.time ? ` ${s.time}` : ""}{s.location ? ` — ${s.location}` : ""}{s.capacity ? ` (${(s.items || []).length}/${s.capacity})` : ""}</option>
              ))}
            </select>
          )}
          {f.shooting_mode === "new" && (
            <SlotPicker endpoint="/content-piloting/shooting-slots" picked={f.new_slot} onPick={(sl) => setF({ ...f, new_slot: sl })} testid="hl-slot-picker" />
          )}
          {f.shooting_mode === "new" && f.new_slot && (
            <p className="text-[11px] text-blue-700" data-testid="hl-slot-chosen">Créneau choisi : {fmtD(f.new_slot.date)} à {f.new_slot.start} — bloqué dans l'agenda à la création.</p>
          )}
        </div>
      )}
      <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={set("description")} data-testid="hl-description" /></div>
      <div><Label>Notes de préparation</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="hl-notes" /></div>
      <Button className="w-full bg-[#0B132B] hover:bg-[#141E3C] text-white" disabled={busy} onClick={save} data-testid="hl-save">{busy ? "Enregistrement…" : h.id ? "Enregistrer" : "Créer le temps fort"}</Button>
    </Modal>
  );
}

function ItemModal({ clients, highlights, item, defaultClient, onClose, onSaved }) {
  const it = item || {};
  const [f, setF] = useState({
    client_id: it.client_id || defaultClient || "", title: it.title || "", highlight_id: it.highlight_id || "",
    type: it.type || "reel", diffusion_date: it.diffusion_date || "", creation_deadline: it.creation_deadline || "",
    shooting_needed: !!it.shooting_needed, shooting_date: it.shooting_date || "", responsible: it.responsible || "",
    brief: it.brief || "", links: it.links || "", status: it.status || "a_creer", notes: it.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const hlOptions = highlights.filter((h) => !f.client_id || h.client_id === f.client_id);
  const save = async () => {
    if (!f.client_id || !f.title.trim()) { toast.error("Client et titre requis"); return; }
    setBusy(true);
    try {
      const payload = { ...f, highlight_id: f.highlight_id || null };
      if (it.id) await api.patch(`/content-piloting/items/${it.id}`, payload);
      else await api.post("/content-piloting/items", payload);
      toast.success(it.id ? "Contenu mis à jour" : "Contenu créé"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={it.id ? "Modifier le contenu" : "Nouveau contenu"} onClose={onClose} testid="item-modal" wide>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Client</Label>
          <select className={sel} value={f.client_id} onChange={set("client_id")} data-testid="item-client" disabled={!!it.id}>
            <option value="">—</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div><Label>Temps fort associé (facultatif)</Label>
          <select className={sel} value={f.highlight_id || ""} onChange={set("highlight_id")} data-testid="item-highlight">
            <option value="">Aucun</option>
            {hlOptions.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select></div>
      </div>
      <div><Label>Titre du contenu</Label><Input value={f.title} onChange={set("title")} placeholder="Ex : Vidéo teaser, Carrousel pédagogique…" data-testid="item-title" /></div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>Type</Label>
          <select className={sel} value={f.type} onChange={set("type")} data-testid="item-type">
            {Object.entries(TYPE_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </select></div>
        <div><Label>Diffusion souhaitée</Label><Input type="date" value={f.diffusion_date} onChange={set("diffusion_date")} data-testid="item-diffusion" /></div>
        <div><Label>Date limite de création</Label><Input type="date" value={f.creation_deadline} onChange={set("creation_deadline")} data-testid="item-deadline" /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3 items-end">
        <label className="flex items-center gap-2 text-sm text-neutral-600 pb-2">
          <input type="checkbox" className="rounded" checked={f.shooting_needed} onChange={(e) => setF({ ...f, shooting_needed: e.target.checked })} data-testid="item-shooting-needed" />
          Shooting nécessaire
        </label>
        <div><Label>Responsable</Label><Input value={f.responsible} onChange={set("responsible")} data-testid="item-responsible" /></div>
      </div>
      <div><Label>Description / brief</Label><Textarea rows={2} value={f.brief} onChange={set("brief")} data-testid="item-brief" /></div>
      <div><Label>Fichiers ou liens associés</Label><Input value={f.links} onChange={set("links")} placeholder="Liens Drive, inspirations…" data-testid="item-links" /></div>
      {it.id && (
        <div><Label>Statut de production</Label>
          <select className={sel} value={f.status} onChange={set("status")} data-testid="item-status">
            {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
          </select></div>
      )}
      <div><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={set("notes")} data-testid="item-notes" /></div>
      <Button className="w-full bg-[#0B132B] hover:bg-[#141E3C] text-white" disabled={busy} onClick={save} data-testid="item-save">{busy ? "Enregistrement…" : it.id ? "Enregistrer" : "Créer le contenu"}</Button>
    </Modal>
  );
}

function ShootingModal({ clients, items, shooting, defaultClient, onClose, onSaved }) {
  const s = shooting || {};
  const [f, setF] = useState({
    client_id: s.client_id || defaultClient || "", date: s.date || "", time: s.time || "",
    location: s.location || "", duration: s.duration || "", participants: s.participants || "", notes: s.notes || "",
    duration_min: s.duration_min || 120, capacity: s.capacity || "", status: s.status || "planifie",
  });
  const [selected, setSelected] = useState(() => new Set((s.items || []).map((i) => i.id)));
  const [busy, setBusy] = useState(false);
  const candidates = items.filter((i) => i.client_id === f.client_id && (!i.shooting_id || selected.has(i.id)) && !["finalise", "pret_a_publier"].includes(i.status));
  const save = async () => {
    if (!f.client_id || !f.date) { toast.error("Client et date requis"); return; }
    setBusy(true);
    try {
      const payload = { ...f, capacity: f.capacity || null, duration_min: Number(f.duration_min) || 120 };
      let sid = s.id;
      if (sid) await api.patch(`/content-piloting/shootings/${sid}`, payload);
      else sid = (await api.post("/content-piloting/shootings", payload)).data.id;
      await Promise.all([...selected].map((iid) => api.patch(`/content-piloting/items/${iid}`, { shooting_id: sid })));
      toast.success("Shooting enregistré"); onSaved(); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Modal title={s.id ? "Modifier le shooting" : "Programmer un shooting"} onClose={onClose} testid="shooting-modal" wide>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Client</Label>
          <select className={sel} value={f.client_id} onChange={(e) => setF({ ...f, client_id: e.target.value })} data-testid="shooting-client">
            <option value="">—</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select></div>
        <div><Label>Date</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="shooting-date" /></div>
      </div>
      <div className="grid sm:grid-cols-3 gap-3">
        <div><Label>Heure</Label><Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} data-testid="shooting-time" /></div>
        <div><Label>Durée prévue</Label>
          <select className={sel} value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: Number(e.target.value) })} data-testid="shooting-duration-min">
            {[60, 90, 120, 180, 240].map((m) => <option key={m} value={m}>{fmtDur(m)}</option>)}
          </select></div>
        <div><Label>Capacité max (contenus)</Label><Input type="number" min="0" value={f.capacity} placeholder="Illimitée" onChange={(e) => setF({ ...f, capacity: e.target.value })} data-testid="shooting-capacity" /></div>
      </div>
      <SlotPicker endpoint="/content-piloting/shooting-slots" durationMin={Number(f.duration_min) || 120}
        picked={f.date && f.time ? { date: f.date, start: f.time } : null}
        onPick={(sl) => setF({ ...f, date: sl.date, time: sl.start })} testid="shooting-slot-picker" />
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Lieu</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} data-testid="shooting-location" /></div>
        <div><Label>Participants</Label><Input value={f.participants} onChange={(e) => setF({ ...f, participants: e.target.value })} data-testid="shooting-participants" /></div>
      </div>
      {s.id && (
        <div><Label>Statut</Label>
          <select className={sel} value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="shooting-status">
            <option value="planifie">Planifié</option><option value="termine">Terminé</option><option value="annule">Annulé</option>
          </select></div>
      )}
      {f.client_id && candidates.length > 0 && (
        <div>
          <Label>Contenus à tourner (regroupez plusieurs sujets dans le même shooting)</Label>
          <div className="border border-neutral-200 rounded-lg divide-y divide-neutral-100 max-h-44 overflow-y-auto" data-testid="shooting-items">
            {candidates.map((i) => (
              <label key={i.id} className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-neutral-50 cursor-pointer" data-testid={`shooting-item-${i.id}`}>
                <input type="checkbox" className="rounded" checked={selected.has(i.id)}
                  onChange={(e) => { const n = new Set(selected); e.target.checked ? n.add(i.id) : n.delete(i.id); setSelected(n); }} />
                <span className="flex-1">{i.title}</span>
                <Badge meta={TYPE_META} value={i.type} />
              </label>
            ))}
          </div>
        </div>
      )}
      <div><Label>Notes</Label><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="shooting-notes" /></div>
      <Button className="w-full bg-[#0B132B] hover:bg-[#141E3C] text-white" disabled={busy} onClick={save} data-testid="shooting-save">{busy ? "Enregistrement…" : "Enregistrer le shooting"}</Button>
    </Modal>
  );
}

/* ---------------- Tiroir de détail contenu ---------------- */

function ItemDrawer({ item, clients, highlights, shootings, onClose, onChanged }) {
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkable, setLinkable] = useState(null);
  const [pubForm, setPubForm] = useState({ date: item.diffusion_date || "", time: "18:00" });
  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const pub = PUB_META[item.publication_status] || PUB_META.non_creee;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto p-5 space-y-4 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="item-drawer">
        <div className="flex items-start justify-between gap-2">
          <div>
            <h3 className="font-heading font-bold text-lg">{item.title}</h3>
            <p className="text-xs text-neutral-400">{item.client_name} {item.highlight_name ? `· ${item.highlight_name}` : ""}</p>
          </div>
          <button onClick={onClose} className="p-1 text-neutral-400 hover:text-neutral-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Badge meta={TYPE_META} value={item.type} />
          <Badge meta={STATUS_META} value={item.status} />
          {item.shot && <span className="text-[10px] font-medium border rounded-full px-2 py-0.5 bg-teal-50 text-teal-700 border-teal-200">Tourné ✓</span>}
        </div>
        <div className="text-xs space-y-1.5 bg-neutral-50 rounded-xl p-3 border border-neutral-100">
          <p className="flex justify-between"><span className="text-neutral-400">Diffusion souhaitée</span><strong>{fmtD(item.diffusion_date)}</strong></p>
          <p className="flex justify-between"><span className="text-neutral-400">Limite de création</span><strong>{fmtD(item.creation_deadline)}</strong></p>
          <p className="flex justify-between"><span className="text-neutral-400">Responsable</span><strong>{item.responsible || "—"}</strong></p>
          {item.brief && <p className="pt-1 border-t border-neutral-200 text-neutral-600 whitespace-pre-line">{item.brief}</p>}
          {item.links && <p className="text-blue-600 break-all">{item.links}</p>}
        </div>

        {/* Publication liée — statut synchronisé depuis le module CM */}
        <div className="border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="drawer-publication">
          <p className="text-xs font-semibold text-neutral-600">Publication associée</p>
          {item.publication_id ? (
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs">
                <p className="font-medium">{item.publication_title}</p>
                <p className={`font-semibold ${pub.cls}`} data-testid="drawer-pub-status">{pub.label}</p>
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => navigate(`/publications/${item.publication_id}`)} data-testid="drawer-pub-open">
                  Ouvrir <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-neutral-400" onClick={() => act(() => api.post(`/content-piloting/items/${item.id}/unlink-publication`), "Publication détachée")} data-testid="drawer-pub-unlink">Détacher</Button>
              </div>
            </div>
          ) : (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 h-8 text-xs gap-1" data-testid="drawer-pub-link"
                onClick={async () => { setLinkOpen(true); setLinkable(null); const r = await api.get(`/content-piloting/items/${item.id}/linkable-publications`); setLinkable(r.data); }}>
                <Link2 className="w-3.5 h-3.5" /> Lier une publication
              </Button>
              <Button size="sm" className="flex-1 h-8 text-xs gap-1 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" data-testid="drawer-pub-create"
                onClick={() => act(() => api.post(`/content-piloting/items/${item.id}/create-publication`, pubForm), "Publication créée dans le calendrier éditorial et liée")}>
                <FilePlus2 className="w-3.5 h-3.5" /> Créer la publication
              </Button>
            </div>
          )}
        </div>

        {/* Shooting */}
        <div className="border border-neutral-200 rounded-xl p-3 space-y-2" data-testid="drawer-shooting">
          <p className="text-xs font-semibold text-neutral-600">Shooting</p>
          <div className="flex items-center gap-2">
            <select className={`${sel} !py-1.5 text-xs flex-1`} value={item.shooting_id || ""} data-testid="drawer-shooting-select"
              onChange={(e) => act(() => api.patch(`/content-piloting/items/${item.id}`, { shooting_id: e.target.value || null }), e.target.value ? "Contenu rattaché au shooting" : "Contenu détaché du shooting")}>
              <option value="">Aucun</option>
              {shootings.filter((s) => s.client_id === item.client_id).map((s) => (
                <option key={s.id} value={s.id}>{fmtD(s.date)}{s.time ? ` ${s.time}` : ""}{s.location ? ` — ${s.location}` : ""}</option>
              ))}
            </select>
            <label className="flex items-center gap-1.5 text-xs text-neutral-500 whitespace-nowrap" data-testid="drawer-shot-wrap">
              <input type="checkbox" className="rounded" checked={!!item.shot} data-testid="drawer-shot"
                onChange={(e) => act(() => api.patch(`/content-piloting/items/${item.id}`, { shot: e.target.checked }), e.target.checked ? "Marqué comme tourné" : "Coche retirée")} />
              Tourné
            </label>
          </div>
        </div>

        <label className="flex items-center gap-2 text-xs text-neutral-600 border border-neutral-200 rounded-xl px-3 py-2.5 cursor-pointer" data-testid="drawer-created-wrap">
          <input type="checkbox" className="rounded accent-emerald-600" checked={!!item.created} data-testid="drawer-created"
            onChange={(e) => act(() => api.patch(`/content-piloting/items/${item.id}`, { created: e.target.checked }), e.target.checked ? "Contenu créé ✓ (statut de production inchangé)" : "Coche « créé » retirée")} />
          Contenu créé — coche indépendante du statut de production
        </label>

        <div className="grid grid-cols-2 gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditOpen(true)} data-testid="drawer-edit">Modifier la fiche</Button>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1" data-testid="drawer-reuse"
            onClick={() => act(async () => { const r = await api.post(`/content-piloting/items/${item.id}/reuse`); toast.success(`Nouvelle tâche créée : « ${r.data.title} » (publication d'origine inchangée)`); }, null)}>
            <RotateCcw className="w-3.5 h-3.5" /> Réutiliser ce contenu
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate(`/clients/${item.client_id}`)} data-testid="drawer-open-client">Ouvrir le client</Button>
          <Button variant="outline" size="sm" className="h-8 text-xs text-red-500 hover:text-red-600" data-testid="drawer-delete"
            onClick={() => { if (window.confirm(`Supprimer le contenu « ${item.title} » ?`)) act(() => api.delete(`/content-piloting/items/${item.id}`), "Contenu supprimé").then(() => onClose()); }}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
        {item.notes && <p className="text-xs text-neutral-400 italic">{item.notes}</p>}
      </div>

      {editOpen && <ItemModal clients={clients} highlights={highlights} item={item} onClose={() => setEditOpen(false)} onSaved={() => { onChanged(); }} />}
      {linkOpen && (
        <Modal title="Lier une publication existante" onClose={() => setLinkOpen(false)} testid="link-pub-modal">
          {linkable === null && <p className="text-xs text-neutral-400 animate-pulse">Chargement…</p>}
          {linkable !== null && linkable.length === 0 && <p className="text-xs text-neutral-400" data-testid="link-pub-empty">Aucune publication rattachable pour ce client — créez-la depuis ce contenu ou le calendrier éditorial.</p>}
          <div className="space-y-1.5 max-h-72 overflow-y-auto">
            {(linkable || []).map((p) => (
              <button key={p.id} data-testid={`link-pub-${p.id}`} className="w-full flex items-center justify-between text-xs border border-neutral-200 rounded-lg px-3 py-2 hover:border-[#1D4ED8]/50 hover:bg-neutral-50 text-left"
                onClick={() => act(async () => { await api.post(`/content-piloting/items/${item.id}/link-publication`, { publication_id: p.id }); setLinkOpen(false); }, "Publication liée — son statut se synchronise automatiquement")}>
                <span className="font-medium truncate">{p.title}</span>
                <span className="text-neutral-400 shrink-0 ml-2">{fmtD(p.date)}</span>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ---------------- Fiche client ---------------- */

function ClientDetail({ client, items, highlights, shootings, onChanged, clients, navigate }) {
  const [tab, setTab] = useState("planning");
  const [modal, setModal] = useState(null); // "highlight" | "item" | "shooting"
  const [search, setSearch] = useState("");
  const [quarter, setQuarter] = useState(() => `${Math.floor(new Date().getMonth() / 3) + 1}`);
  const [openHlId, setOpenHlId] = useState(null);
  const cItems = items.filter((i) => i.client_id === client.id);
  const cHighlights = highlights.filter((h) => h.client_id === client.id);
  const cShootings = shootings.filter((s) => s.client_id === client.id);
  const today = new Date().toISOString().slice(0, 10);
  const nextShooting = cShootings.find((s) => s.date >= today);
  const aCreer = cItems.filter((i) => !["finalise", "pret_a_publier"].includes(i.status)).length;
  const aValider = cItems.filter((i) => i.publication_status === "a_valider").length;
  const done = cItems.filter((i) => ["finalise", "pret_a_publier"].includes(i.status) || ["publiee"].includes(i.publication_status));
  const doneHighlights = cHighlights.filter((h) => h.status === "termine");
  const year = new Date().getFullYear();
  const qMonths = { 1: [1, 2, 3], 2: [4, 5, 6], 3: [7, 8, 9], 4: [10, 11, 12] }[quarter] || [1, 2, 3];
  const qKey = (m) => `${year}-${String(m).padStart(2, "0")}`;
  const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  return (
    <div className="space-y-4" data-testid="piloting-client-detail">
      <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 flex flex-wrap items-center gap-x-6 gap-y-2" data-testid="piloting-client-header">
        <div>
          <h2 className="font-heading text-lg font-bold">{client.name}</h2>
          <p className="text-[11px] text-neutral-400">Pilotage de sa communication</p>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-neutral-500">
          <span>Contenus à créer : <strong className="text-[#0F172A]" data-testid="cd-a-creer">{aCreer}</strong></span>
          <span>Publications à valider : <strong className="text-[#0F172A]" data-testid="cd-a-valider">{aValider}</strong></span>
          <span>Prochain shooting : <strong className="text-[#0F172A]" data-testid="cd-shooting">{nextShooting ? `${fmtD(nextShooting.date)}${nextShooting.time ? ` ${nextShooting.time}` : ""}` : "—"}</strong></span>
        </div>
        <div className="flex-1" />
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setModal("highlight")} data-testid="cd-add-highlight"><Sparkles className="w-3.5 h-3.5" /> Événement / offre</Button>
          <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setModal("shooting")} data-testid="cd-add-shooting"><Camera className="w-3.5 h-3.5" /> Shooting</Button>
          <Button size="sm" className="h-8 text-xs gap-1 bg-[#0B132B] text-white" onClick={() => setModal("item")} data-testid="cd-add-item"><Plus className="w-3.5 h-3.5" /> Contenu</Button>
        </div>
      </div>

      <div className="flex items-center gap-5 border-b border-neutral-100" data-testid="cd-tabs">
        {[["planning", "Planning"], ["contenus", "Contenus"], ["historique", "Historique"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`cd-tab-${id}`}
            className={`pb-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>{l}</button>
        ))}
        {tab === "planning" && (
          <div className="ml-auto flex items-center gap-1 pb-1">
            {[1, 2, 3, 4].map((q) => (
              <button key={q} onClick={() => setQuarter(String(q))} data-testid={`cd-quarter-${q}`}
                className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border ${quarter === String(q) ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-neutral-400 border-neutral-200"}`}>T{q}</button>
            ))}
          </div>
        )}
      </div>

      {tab === "planning" && (
        <div className="grid md:grid-cols-3 gap-3" data-testid="cd-quarterly">
          {qMonths.map((m) => {
            const key = qKey(m);
            const hls = cHighlights.filter((h) => (h.month || (h.event_date || "").slice(0, 7)) === key);
            return (
              <div key={key} className="bg-white border border-[#E2E8F0] rounded-xl p-3 space-y-2" data-testid={`cd-month-${key}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  const hid = e.dataTransfer.getData("text/plain");
                  const hl = cHighlights.find((x) => x.id === hid);
                  if (!hl || (hl.month || (hl.event_date || "").slice(0, 7)) === key) return;
                  if (!window.confirm(`Déplacer « ${hl.name} » vers ${monthNames[m - 1]} ${year} ?\n\nSeul le mois de pilotage change — les dates de l'événement, du shooting et des publications ne sont pas modifiées.`)) return;
                  api.patch(`/content-piloting/highlights/${hid}`, { month: key })
                    .then(() => { toast.success("Mois de pilotage mis à jour"); onChanged(); })
                    .catch((err) => toast.error(apiError(err)));
                }}>
                <p className="text-xs font-bold text-neutral-700">{monthNames[m - 1]} {year}</p>
                {hls.length === 0 && <p className="text-[11px] text-neutral-300">À prévoir</p>}
                {hls.map((h) => {
                  const hItems = cItems.filter((i) => i.highlight_id === h.id);
                  const hCreated = hItems.filter(isCreated).length;
                  const hShooting = cShootings.find((s) => s.id === h.shooting_id);
                  return (
                  <div key={h.id} draggable data-testid={`cd-hl-${h.id}`}
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", h.id)}
                    onClick={() => setOpenHlId(h.id)}
                    className="border border-violet-100 bg-violet-50/40 rounded-lg p-2 cursor-pointer hover:border-violet-300 hover:shadow-sm transition-all">
                    <p className="text-xs font-semibold flex items-center justify-between">{h.name}
                      <span className="flex items-center gap-1.5">
                        <span className="text-[9px] text-violet-500">{HL_TYPES[h.type]}</span>
                        <button data-testid={`cd-hl-del-${h.id}`} className="p-0.5 text-neutral-300 hover:text-red-500 transition-colors"
                          onClick={async (e) => { e.stopPropagation(); if (window.confirm(`Supprimer le temps fort « ${h.name} » ? Les contenus associés seront conservés.`)) { try { await api.delete(`/content-piloting/highlights/${h.id}`); toast.success("Temps fort supprimé"); onChanged(); } catch (err) { toast.error(apiError(err)); } } }}>
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </span></p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Badge meta={EVENT_STAGES} value={eventStageKey(hItems)} />
                      <span className="text-[9px] text-neutral-500" data-testid={`cd-hl-progress-${h.id}`}>{hCreated}/{hItems.length} créés</span>
                    </div>
                    {hItems.length > 0 && (
                      <div className="mt-1"><PubProgressBar items={hItems} testid={`cd-hl-pubbar-${h.id}`} /></div>
                    )}
                    {h.event_date && <p className="text-[10px] text-neutral-400 mt-0.5">Événement : {fmtD(h.event_date)}</p>}
                    {h.offer && <p className="text-[10px] text-emerald-700 mt-0.5">Offre : {h.offer}</p>}
                    <p className="text-[10px] text-amber-600 mt-0.5">Shooting : {hShooting ? `${fmtD(hShooting.date)}${hShooting.time ? ` ${hShooting.time}` : ""}` : h.shooting_date ? fmtD(h.shooting_date) : h.shooting_needed ? "à programmer" : "non requis"}</p>
                  </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {tab === "contenus" && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-hidden" data-testid="cd-contenus">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[10px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
              <th className="px-4 py-2 font-medium">Contenu</th><th className="px-2 py-2 font-medium">Type</th>
              <th className="px-2 py-2 font-medium">Diffusion</th><th className="px-2 py-2 font-medium">Production</th>
              <th className="px-2 py-2 font-medium">Publication</th><th className="px-2 py-2 font-medium">Temps fort</th>
            </tr></thead>
            <tbody>
              {cItems.map((i) => (
                <tr key={i.id} className="border-b border-neutral-50 hover:bg-neutral-50/60" data-testid={`cd-row-${i.id}`}>
                  <td className="px-4 py-2 font-medium">{i.title}{i.shot && <span className="text-teal-600 text-[10px] ml-1.5">tourné ✓</span>}</td>
                  <td className="px-2 py-2"><Badge meta={TYPE_META} value={i.type} /></td>
                  <td className="px-2 py-2 text-neutral-500">{fmtD(i.diffusion_date)}</td>
                  <td className="px-2 py-2"><Badge meta={STATUS_META} value={i.status} /></td>
                  <td className="px-2 py-2"><span className={`text-[11px] font-medium ${PUB_META[i.publication_status]?.cls}`}>{PUB_META[i.publication_status]?.label}</span></td>
                  <td className="px-2 py-2 text-neutral-400">{i.highlight_name || "—"}</td>
                </tr>
              ))}
              {cItems.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-neutral-300">Aucun contenu — ajoutez-en un avec le bouton ci-dessus.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "historique" && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-3" data-testid="cd-historique">
          <Input placeholder="Rechercher dans l'historique…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-8 text-xs max-w-xs" data-testid="cd-history-search" />
          {doneHighlights.filter((h) => !search || h.name.toLowerCase().includes(search.toLowerCase())).map((h) => (
            <div key={h.id} className="border border-neutral-100 rounded-lg p-3" data-testid={`cd-hist-hl-${h.id}`}>
              <p className="text-xs font-semibold">{h.name} <span className="text-[10px] text-neutral-400 font-normal">{HL_TYPES[h.type]} · {h.event_date ? fmtD(h.event_date) : h.month}</span></p>
              {h.offer && <p className="text-[10px] text-neutral-400 mt-0.5">Offre : {h.offer}</p>}
            </div>
          ))}
          {done.filter((i) => !search || i.title.toLowerCase().includes(search.toLowerCase())).map((i) => (
            <div key={i.id} className="flex items-center justify-between border border-neutral-100 rounded-lg px-3 py-2" data-testid={`cd-hist-item-${i.id}`}>
              <div>
                <p className="text-xs font-medium">{i.title}</p>
                <p className="text-[10px] text-neutral-400">
                  {i.publication_status === "publiee" ? `Diffusé${i.diffusion_date ? ` le ${fmtD(i.diffusion_date)}` : ""}` : STATUS_META[i.status]?.label}
                </p>
              </div>
              <Badge meta={TYPE_META} value={i.type} />
            </div>
          ))}
          {done.length === 0 && doneHighlights.length === 0 && <p className="text-xs text-neutral-300 text-center py-4">Aucun historique pour l'instant.</p>}
        </div>
      )}

      {modal === "highlight" && <HighlightModal clients={clients} shootings={cShootings} defaultClient={client.id} onClose={() => setModal(null)} onSaved={onChanged} />}
      {modal === "item" && <ItemModal clients={clients} highlights={cHighlights} defaultClient={client.id} onClose={() => setModal(null)} onSaved={onChanged} />}
      {modal === "shooting" && <ShootingModal clients={clients} items={cItems} defaultClient={client.id} onClose={() => setModal(null)} onSaved={onChanged} />}
      {openHlId && <HighlightDrawer highlightId={openHlId} shootings={cShootings} onClose={() => setOpenHlId(null)} onChanged={onChanged} />}
    </div>
  );
}

/* ================= Page principale ================= */

export default function ContentPiloting() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("client") ? "client" : "global");
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [shootings, setShootings] = useState([]);
  const [kpi, setKpi] = useState(null);
  const [mode, setMode] = useState("table"); // table | kanban | calendrier
  const [modal, setModal] = useState(null); // "highlight" | "item" | "shooting"
  const [openItemId, setOpenItemId] = useState(null);
  const [clientId, setClientId] = useState(searchParams.get("client") || "");
  const [filters, setFilters] = useState({ client_id: "", type: "", status: "", responsible: "", highlight_id: "", shooting_id: "", period: "tout" });
  const [dragOver, setDragOver] = useState(null);
  const [openShooting, setOpenShooting] = useState(null);
  const [openHlId, setOpenHlId] = useState(null);

  const load = useCallback(() => {
    api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {});
    api.get("/content-piloting/overview").then((r) => setKpi(r.data)).catch(() => {});
    api.get("/content-piloting/highlights").then((r) => setHighlights(r.data || [])).catch(() => {});
    api.get("/content-piloting/shootings").then((r) => setShootings(r.data || [])).catch(() => {});
  }, []);

  const loadItems = useCallback(() => {
    const now = new Date();
    let frm = "", to = "";
    if (filters.period === "mois") {
      frm = isoMonth(monthKey(0)) + "-01";
      to = isoMonth(monthKey(1)) + "-01";
    } else if (filters.period === "trimestre") {
      // mois en cours + 2 prochains mois (défaut du brief)
      frm = isoMonth(monthKey(0)) + "-01";
      to = isoMonth(monthKey(3)) + "-01";
    }
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && k !== "period") params[k] = v; });
    if (frm) { params.frm = frm; params.to = to; }
    if (clientId) params.client_id = clientId;
    api.get("/content-piloting/items", { params }).then((r) => setItems(r.data || [])).catch(() => {});
  }, [filters, clientId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadItems(); }, [loadItems]);

  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); loadItems(); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const clientName = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c.name])), [clients]);
  const hlName = useMemo(() => Object.fromEntries(highlights.map((h) => [h.id, h.name])), [highlights]);
  const enriched = items.map((i) => ({ ...i, client_name: clientName[i.client_id] || "", highlight_name: hlName[i.highlight_id] || "" }));
  const openItem = openItemId ? enriched.find((i) => i.id === openItemId) || null : null;
  const selectedClient = clients.find((c) => c.id === clientId);

  const openClient = (cid) => {
    setClientId(cid); setView("client");
    setSearchParams((p) => { const n = new URLSearchParams(p); n.set("client", cid); return n; }, { replace: true });
  };

  const kpis = kpi ? [
    ["À créer", kpi.a_creer, "text-neutral-700"], ["Shootings à organiser", kpi.shootings_a_organiser, "text-amber-600"],
    ["En production", kpi.en_production, "text-indigo-600"], ["À faire valider", kpi.a_faire_valider, "text-orange-600"],
    ["Prêts à publier", kpi.prets_a_publier, "text-green-600"], ["Programmées", kpi.programmees, "text-blue-600"],
    ["En retard", kpi.en_retard, "text-red-600"],
  ] : [];

  const KANBAN = ["a_creer", "shooting_a_programmer", "shooting_programme", "en_production", "finalise", "pret_a_publier"];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4" data-testid="piloting-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold" data-testid="piloting-title">Pilotage de contenu</h1>
          <p className="text-xs text-neutral-400">Quoi communiquer, quoi produire, quels shootings organiser — sans double saisie avec le calendrier éditorial.</p>
        </div>
        <div className="flex-1" />
        <div className="flex rounded-full border border-[#E2E8F0] bg-white overflow-hidden" data-testid="piloting-view-switch">
          {[["global", "Tous les contenus", LayoutGrid], ["client", "Par client", Users]].map(([id, l, Icon]) => (
            <button key={id} onClick={() => { setView(id); if (id === "global") { setClientId(""); setSearchParams({}, { replace: true }); } }} data-testid={`piloting-view-${id}`}
              className={`flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium transition-colors ${view === id ? "bg-[#0B132B] text-white" : "text-neutral-500 hover:bg-neutral-50"}`}>
              <Icon className="w-3.5 h-3.5" /> {l}
            </button>
          ))}
        </div>
      </div>

      {view === "global" && (
        <>
          {kpi && (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2" data-testid="piloting-kpis">
              {kpis.map(([l, v, cls], i) => (
                <div key={l} className="bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5" data-testid={`piloting-kpi-${i}`}>
                  <p className="text-[10px] text-neutral-400 leading-tight">{l}</p>
                  <p className={`font-heading font-bold text-xl mt-0.5 ${cls}`}>{v}</p>
                </div>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 bg-white border border-[#E2E8F0] rounded-xl px-3 py-2.5" data-testid="piloting-filters">
            <select className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={filters.client_id} data-testid="filter-client"
              onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}>
              <option value="">Tous les clients</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={filters.type} data-testid="filter-type"
              onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
              <option value="">Tous types</option>
              {Object.entries(TYPE_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
            </select>
            <select className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={filters.status} data-testid="filter-status"
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">Tous statuts</option>
              {Object.entries(STATUS_META).map(([v, m]) => <option key={v} value={v}>{m.label}</option>)}
            </select>
            <select className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white max-w-36" value={filters.highlight_id} data-testid="filter-highlight"
              onChange={(e) => setFilters({ ...filters, highlight_id: e.target.value })}>
              <option value="">Tous les événements</option>
              {highlights.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            <select className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white max-w-36" value={filters.shooting_id} data-testid="filter-shooting"
              onChange={(e) => setFilters({ ...filters, shooting_id: e.target.value })}>
              <option value="">Tous les shootings</option>
              {shootings.map((s) => <option key={s.id} value={s.id}>{clientName[s.client_id] || ""} — {fmtD(s.date)}</option>)}
            </select>
            <Input className="!h-8 !w-36 text-xs" placeholder="Responsable" value={filters.responsible} data-testid="filter-responsible"
              onChange={(e) => setFilters({ ...filters, responsible: e.target.value })} />
            <select className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs bg-white" value={filters.period} data-testid="filter-period"
              onChange={(e) => setFilters({ ...filters, period: e.target.value })}>
              <option value="mois">Mois en cours</option>
              <option value="trimestre">Mois en cours + 2 suivants</option>
              <option value="tout">Tout</option>
            </select>
            <div className="flex-1" />
            <div className="flex rounded-lg border border-neutral-200 overflow-hidden">
              {[["table", List], ["kanban", KanbanSquare], ["calendrier", CalendarDays], ["shootings", Camera]].map(([id, Icon]) => (
                <button key={id} onClick={() => setMode(id)} data-testid={`piloting-mode-${id}`}
                  className={`px-2.5 py-1.5 ${mode === id ? "bg-[#0B132B] text-white" : "text-neutral-400 hover:bg-neutral-50"}`}><Icon className="w-3.5 h-3.5" /></button>
              ))}
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setModal("highlight")} data-testid="piloting-add-highlight"><Sparkles className="w-3.5 h-3.5" /> Temps fort</Button>
            <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setModal("shooting")} data-testid="piloting-add-shooting"><Camera className="w-3.5 h-3.5" /> Shooting</Button>
            <Button size="sm" className="h-8 text-xs gap-1 bg-[#0B132B] text-white" onClick={() => setModal("item")} data-testid="piloting-add-item"><Plus className="w-3.5 h-3.5" /> Contenu</Button>
          </div>

          {mode === "table" && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto" data-testid="piloting-table">
              <table className="w-full text-xs min-w-[760px]">
                <thead><tr className="text-left text-[10px] uppercase tracking-wide text-neutral-400 border-b border-neutral-100">
                  <th className="pl-4 pr-1 py-2.5 font-medium">Créé</th><th className="px-2 py-2.5 font-medium">Client</th><th className="px-2 py-2.5 font-medium">Sujet / Contenu</th>
                  <th className="px-2 py-2.5 font-medium">Date prévue</th><th className="px-2 py-2.5 font-medium">Deadline</th><th className="px-2 py-2.5 font-medium">Type</th>
                  <th className="px-2 py-2.5 font-medium">Statut</th><th className="px-2 py-2.5 font-medium">Publication</th>
                  <th className="px-2 py-2.5 font-medium text-right">Action</th>
                </tr></thead>
                <tbody>
                  {enriched.map((i) => (
                    <tr key={i.id} className={`border-b border-neutral-50 hover:bg-neutral-50/60 ${i.diffusion_date && i.diffusion_date < today && !["finalise", "pret_a_publier"].includes(i.status) && i.publication_status !== "publiee" ? "bg-red-50/40" : ""}`} data-testid={`piloting-row-${i.id}`}>
                      <td className="pl-4 pr-1 py-2.5">
                        <input type="checkbox" className="rounded accent-emerald-600" checked={!!i.created} data-testid={`piloting-created-${i.id}`}
                          onChange={(e) => act(() => api.patch(`/content-piloting/items/${i.id}`, { created: e.target.checked }), e.target.checked ? "Contenu créé ✓" : "Coche retirée")} />
                      </td>
                      <td className="px-2 py-2.5">
                        <button className="font-semibold text-[#0F172A] hover:underline" onClick={() => openClient(i.client_id)} data-testid={`piloting-client-${i.id}`}>{i.client_name}</button>
                        {i.highlight_name && <button className="block text-[10px] text-violet-500 hover:text-violet-700 hover:underline text-left" onClick={() => setOpenHlId(i.highlight_id)} data-testid={`piloting-event-${i.id}`}>{i.highlight_name}</button>}
                      </td>
                      <td className="px-2 py-2.5 font-medium">{i.title}{i.shot && <span className="text-teal-600 text-[10px] ml-1.5">tourné ✓</span>}</td>
                      <td className="px-2 py-2.5 text-neutral-500">{fmtD(i.diffusion_date)}</td>
                      <td className="px-2 py-2.5" data-testid={`piloting-deadline-${i.id}`}>
                        <span className={`font-semibold ${i.creation_deadline && i.creation_deadline < today && !["finalise", "pret_a_publier"].includes(i.status) && !i.created ? "text-red-600" : "text-neutral-700"}`}>{fmtD(i.creation_deadline)}</span>
                      </td>
                      <td className="px-2 py-2.5"><Badge meta={TYPE_META} value={i.type} /></td>
                      <td className="px-2 py-2.5"><Badge meta={STATUS_META} value={i.status} /></td>
                      <td className="px-2 py-2.5"><span className={`text-[11px] font-medium ${PUB_META[i.publication_status]?.cls}`}>{PUB_META[i.publication_status]?.label}</span></td>
                      <td className="px-2 py-2.5 text-right">
                        <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => (i.highlight_id ? setOpenHlId(i.highlight_id) : setOpenItemId(i.id))} data-testid={`piloting-open-${i.id}`}>Ouvrir</Button>
                      </td>
                    </tr>
                  ))}
                  {enriched.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-neutral-300" data-testid="piloting-empty">Aucun contenu — créez un temps fort ou un contenu.</td></tr>}
                </tbody>
              </table>
            </div>
          )}

          {mode === "kanban" && (
            <div className="flex gap-3 overflow-x-auto pb-3" data-testid="piloting-kanban">
              {KANBAN.map((st) => {
                const list = enriched.filter((i) => i.status === st);
                return (
                  <div key={st} data-testid={`piloting-kanban-${st}`}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(st); }}
                    onDragLeave={() => setDragOver(null)}
                    onDrop={(e) => {
                      e.preventDefault(); setDragOver(null);
                      const id = e.dataTransfer.getData("text/plain");
                      const it = enriched.find((x) => x.id === id);
                      if (it && it.status !== st) act(() => api.patch(`/content-piloting/items/${id}`, { status: st }), `« ${it.title} » → ${STATUS_META[st].label}`);
                    }}
                    className={`w-64 shrink-0 bg-neutral-50 border rounded-xl flex flex-col transition-colors ${dragOver === st ? "border-[#1D4ED8] bg-blue-50/50" : "border-neutral-200"}`}>
                    <p className="text-xs font-bold text-neutral-600 px-3 py-2.5 border-b border-neutral-200">{STATUS_META[st].label} <span className="text-neutral-300">{list.length}</span></p>
                    <div className="p-2 space-y-2">
                      {list.map((i) => (
                        <div key={i.id} draggable data-testid={`piloting-kcard-${i.id}`}
                          onDragStart={(e) => e.dataTransfer.setData("text/plain", i.id)}
                          onClick={() => (i.highlight_id ? setOpenHlId(i.highlight_id) : setOpenItemId(i.id))}
                          className="w-full text-left bg-white border border-neutral-200 rounded-lg px-3 py-2 hover:border-[#93C5FD] hover:shadow-sm transition-all cursor-grab active:cursor-grabbing">
                          <p className="text-xs font-bold truncate">{i.title}</p>
                          <p className="text-[10px] text-neutral-400 truncate">{i.client_name} · {fmtD(i.diffusion_date)}</p>
                          {i.highlight_name && <p className="text-[9px] text-violet-500 truncate">{i.highlight_name}</p>}
                          {i.creation_deadline && (
                            <p className={`text-[10px] mt-0.5 ${i.creation_deadline < today && !["finalise", "pret_a_publier"].includes(i.status) && !i.created ? "text-red-600 font-semibold" : "text-neutral-400"}`}>Deadline : {fmtD(i.creation_deadline)}</p>
                          )}
                          <div className="flex items-center justify-between mt-1">
                            <Badge meta={TYPE_META} value={i.type} />
                            <span className="flex items-center gap-1.5">
                              {i.created && <span className="text-[9px] font-semibold text-emerald-600">Créé ✓</span>}
                              <span className={`text-[9px] font-medium ${PUB_META[i.publication_status]?.cls}`}>{PUB_META[i.publication_status]?.label}</span>
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {mode === "calendrier" && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 space-y-2" data-testid="piloting-calendar">
              {Object.entries(enriched.filter((i) => i.diffusion_date).sort((a, b) => a.diffusion_date.localeCompare(b.diffusion_date))
                .reduce((acc, i) => { (acc[i.diffusion_date] = acc[i.diffusion_date] || []).push(i); return acc; }, {})).map(([d, list]) => (
                <div key={d} className="flex gap-3 border-b border-neutral-50 pb-2" data-testid={`piloting-calday-${d}`}>
                  <p className="text-xs font-bold text-neutral-500 w-20 shrink-0 pt-1">{fmtD(d)}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {list.map((i) => (
                      <button key={i.id} onClick={() => (i.highlight_id ? setOpenHlId(i.highlight_id) : setOpenItemId(i.id))} data-testid={`piloting-calitem-${i.id}`}
                        className={`text-[10px] font-medium border rounded-full px-2.5 py-1 ${STATUS_META[i.status]?.cls}`}>
                        {i.client_name} · {i.title}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {enriched.filter((i) => i.diffusion_date).length === 0 && <p className="text-xs text-neutral-300 text-center py-6">Aucune date de diffusion définie sur la période.</p>}
              {enriched.some((i) => !i.diffusion_date) && (
                <p className="text-[10px] text-neutral-400 pt-1">+ {enriched.filter((i) => !i.diffusion_date).length} contenu(s) sans date (« À définir », visibles dans le tableau).</p>
              )}
            </div>
          )}

          {mode === "shootings" && (
            <ShootingsView shootings={shootings} clientName={clientName} onOpen={(s) => setOpenShooting(s)} />
          )}

          {kpi && kpi.prochains_shootings.length > 0 && (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="piloting-shootings">
              <p className="text-xs font-bold text-neutral-600 mb-2 flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Prochains shootings</p>
              <div className="flex flex-wrap gap-2">
                {kpi.prochains_shootings.map((s) => (
                  <button key={s.id} onClick={() => { const full = shootings.find((x) => x.id === s.id); if (full) setOpenShooting(full); }} className="text-xs border border-blue-100 bg-blue-50/50 rounded-lg px-3 py-2 text-left hover:border-blue-300 transition-colors" data-testid={`piloting-shooting-${s.id}`}>
                    <p className="font-semibold">{clientName[s.client_id]} — {fmtD(s.date)}{s.time ? ` ${s.time}` : ""}</p>
                    <p className="text-[10px] text-neutral-400">{s.location || "Lieu à définir"} · {(s.done_count ?? 0)}/{(s.items || []).length} tourné(s)</p>
                  </button>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {view === "client" && (
        <>
          {!selectedClient ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="piloting-client-picker">
              {clients.map((c) => (
                <button key={c.id} onClick={() => openClient(c.id)} data-testid={`piloting-pick-${c.id}`}
                  className="bg-white border border-[#E2E8F0] rounded-xl p-4 text-left hover:border-[#93C5FD] hover:shadow-sm transition-all">
                  <p className="font-heading font-bold">{c.name}</p>
                  <p className="text-[11px] text-neutral-400 mt-0.5">
                    {items.filter((i) => i.client_id === c.id && !["finalise", "pret_a_publier"].includes(i.status)).length} contenu(s) en cours ·{" "}
                    {highlights.filter((h) => h.client_id === c.id && h.status !== "termine").length} temps fort(s)
                  </p>
                </button>
              ))}
            </div>
          ) : (
            <>
              <button onClick={() => { setClientId(""); setSearchParams({}, { replace: true }); }} className="text-xs text-neutral-400 hover:text-neutral-900" data-testid="piloting-back">← Tous les clients</button>
              <ClientDetail client={selectedClient} items={enriched} highlights={highlights} shootings={shootings}
                clients={clients} onChanged={() => { load(); loadItems(); }} navigate={navigate} />
            </>
          )}
        </>
      )}

      {modal === "highlight" && <HighlightModal clients={clients} shootings={shootings} onClose={() => setModal(null)} onSaved={() => { load(); }} />}
      {modal === "item" && <ItemModal clients={clients} highlights={highlights} onClose={() => setModal(null)} onSaved={() => { load(); loadItems(); }} />}
      {modal === "shooting" && <ShootingModal clients={clients} items={enriched} onClose={() => setModal(null)} onSaved={() => { load(); loadItems(); }} />}
      {openItem && <ItemDrawer item={openItem} clients={clients} highlights={highlights} shootings={shootings}
        onClose={() => setOpenItemId(null)} onChanged={() => { load(); loadItems(); }} />}
      {openHlId && <HighlightDrawer highlightId={openHlId} shootings={shootings} onClose={() => setOpenHlId(null)} onChanged={() => { load(); loadItems(); }} />}
      {openShooting && (
        <ShootingDrawer shooting={shootings.find((s) => s.id === openShooting.id) || openShooting}
          clientName={clientName[openShooting.client_id] || ""}
          onClose={() => setOpenShooting(null)} onChanged={() => { load(); loadItems(); }} />
      )}
    </div>
  );
}
